import { OnboardingProductType } from "@redotech/cockroachdb-codegen/congodb/onboarding-product-type";
import { OnboardingType } from "@redotech/cockroachdb-codegen/congodb/onboarding-type";
import {
  createStringEnumSchema,
  selfServeOnboardingStepsConfigurationFieldSchemas,
} from "@redotech/redo-onboarding-common/self-serve-onboarding-configuration";
import * as z from "zod";

const productTypeSchema = createStringEnumSchema(OnboardingProductType);

const stepsConfigurationSchema = z.object({
  steps: z.record(z.string(), z.array(z.string())),
  // Rows written by the pre-#53461 upsert can lack autoPush; default on read
  // so output validation doesn't 500 the settings panel for those merchants.
  autoPush: z.boolean().default(false),
  ...selfServeOnboardingStepsConfigurationFieldSchemas,
  customCoveragePrice: z.number().int().nullish(),
  onboardingType: createStringEnumSchema(OnboardingType).nullish(),
});

const createdBySchema = z.object({
  userId: z.string().optional(),
  name: z.string().optional(), // Optional - may not be populated
  email: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  department: z.string().optional(),
});

const assigneeSchema = z.object({
  email: z.string(),
  firstName: z.string().optional(), // Optional - may not be in employees collection
  lastName: z.string().optional(),
  department: z.string().optional(),
  name: z.string().optional(), // Computed full name
});

const hubspotMetadataSchema = z.object({
  dealId: z.string().nullable(),
  companyId: z.string().nullable(),
  lastSyncedAt: z.string().nullable(),
});

const onboardingStatusSchema = z.enum([
  "not-started",
  "in-progress",
  "completed",
]);

// Onboarding progress details from masters collection
const onboardingProgressDetailsSchema = z.object({
  status: onboardingStatusSchema,
  completionPercent: z.number().nullable(),
  completionDate: z.string().nullable(),
  totalSessions: z.number().nullable(),
  durationSeconds: z.number().nullable(),
  lastUpdatedAt: z.string().nullable(),
});

// Single configuration document (flat structure)
const configurationSchema = z.object({
  merchantId: z.string().nullable(), // null for pre-install configs
  merchantName: z.string(),
  product: productTypeSchema,
  stepsConfig: stepsConfigurationSchema,
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: createdBySchema.optional(), // Optional for legacy records
  notes: z.string().nullish(),
  onboardingStatus: onboardingStatusSchema,
  onboardingProgress: onboardingProgressDetailsSchema.optional(),
  assignees: z.array(assigneeSchema).optional().default([]),
  publicDomain: z.string().optional(), // for pre-install configs
  hubspotMetadata: hubspotMetadataSchema.optional(), // HubSpot integration metadata
});

// Get single configuration by merchantId + product
export const getOnboardingConfiguration = {
  name: "getOnboardingConfiguration" as const,
  input: z.object({
    merchantId: z.string().min(1),
    product: productTypeSchema,
  }),
  output: z.object({
    found: z.boolean(),
    configuration: configurationSchema.nullable(),
  }),
};

export type ProductType = z.infer<typeof productTypeSchema>;
export type StepsConfiguration = z.infer<typeof stepsConfigurationSchema>;
export type Configuration = z.infer<typeof configurationSchema>;
export type OnboardingStatus = z.infer<typeof onboardingStatusSchema>;
export type GetOnboardingConfigurationInput = z.infer<
  typeof getOnboardingConfiguration.input
>;
export type GetOnboardingConfigurationOutput = z.infer<
  typeof getOnboardingConfiguration.output
>;
